import { Num, T, Var } from 'gt-react';

import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import type { RowError, ResultsToUploadResult } from './resultsToUpload';

/** Row errors listed before the rest are summarized as a count. */
const MAX_ROW_ERRORS = 50;

type Invalid = Extract<ResultsToUploadResult, { status: 'invalid' }>;

/** Why no CSV was produced: the layout, the file's columns, or its rows. */
export function InvalidResults({ result }: { result: Invalid }) {
  const shown = result.rowErrors.slice(0, MAX_ROW_ERRORS);
  const hidden = result.rowErrors.length - shown.length;
  return (
    <AlertContainer variant="destructive">
      <AlertTitle>
        <T context="error title when a supplier workbook cannot be converted">
          No CSV was produced
        </T>
      </AlertTitle>
      <AlertDescription>
        {result.layoutProblems.length > 0 ? (
          <ul className="list-disc pl-4">
            {result.layoutProblems.map((problem) => (
              <li key={`${problem.column ?? ''}-${problem.message}`}>
                <T context="a problem in an upload layout's column mapping">
                  Layout column <Var>{problem.column ?? '—'}</Var>:{' '}
                  <Var>{problem.message}</Var>
                </T>
              </li>
            ))}
          </ul>
        ) : null}
        {result.missingLookup ? (
          <p>
            <T context="error: the supplier layout needs a second file that was not provided">
              This supplier needs its <Var>{result.missingLookup}</Var> file
              too. Drop the results file again and add it.
            </T>
          </p>
        ) : null}
        {result.missingHeaders.length > 0 ? (
          <p>
            <T context="error listing input columns the layout reads but the workbook lacks">
              The workbook is missing these columns:{' '}
              <Var>{result.missingHeaders.join(', ')}</Var>
            </T>
          </p>
        ) : null}
        {shown.length > 0 ? (
          <ul className="list-disc pl-4">
            {shown.map((error) => (
              <li
                key={`${error.sheetRow}-${error.column ?? ''}-${error.header ?? ''}`}
              >
                <RowErrorText error={error} />
              </li>
            ))}
          </ul>
        ) : null}
        {hidden > 0 ? (
          <p>
            <T context="count of further row errors not listed">
              …and <Num>{hidden}</Num> more.
            </T>
          </p>
        ) : null}
      </AlertDescription>
    </AlertContainer>
  );
}

type Problem = RowError['problem'];

/** One sentence per problem; `where` is the input header or upload column. */
const ROW_ERROR_TEXT: Record<
  Problem,
  (error: RowError, where: string) => React.ReactNode
> = {
  'error-cell': (error, where) => (
    <T context="row error: an Excel error value in a column the layout reads">
      Excel row <Var>{error.sheetRow}</Var>: <Var>{where}</Var> is an Excel
      error (<Var>{error.value}</Var>)
    </T>
  ),
  blank: (error, where) => (
    <T context="row error: a column a formula reads is blank">
      Excel row <Var>{error.sheetRow}</Var>: <Var>{where}</Var> is blank
    </T>
  ),
  'not-a-number': (error, where) => (
    <T context="row error: a value that should be a number is not">
      Excel row <Var>{error.sheetRow}</Var>: <Var>{where}</Var> is not a number
      (<Var>{error.value}</Var>)
    </T>
  ),
  'bad-date': (error, where) => (
    <T context="row error: a value that should be a date is not">
      Excel row <Var>{error.sheetRow}</Var>: <Var>{where}</Var> is not a date (
      <Var>{error.value}</Var>)
    </T>
  ),
  'formula-text': (error, where) => (
    <T context="row error: a text value that would run as a spreadsheet formula">
      Excel row <Var>{error.sheetRow}</Var>: <Var>{where}</Var> starts with a
      formula character (<Var>{error.value}</Var>)
    </T>
  ),
  'no-lookup-match': (error, where) => (
    <T context="row error: the row's key has no row in the supplier's lookup file">
      Excel row <Var>{error.sheetRow}</Var>: no row in the lookup file matches{' '}
      <Var>{where}</Var> <Var>{error.value}</Var>
    </T>
  ),
  'ambiguous-lookup': (error, where) => (
    <T context="row error: the row's key is on more than one row of the supplier's lookup file">
      Excel row <Var>{error.sheetRow}</Var>: more than one row in the lookup
      file has <Var>{where}</Var> <Var>{error.value}</Var>
    </T>
  ),
  'divide-by-zero': (error, where) => (
    <T context="row error: a formula divides by zero">
      Excel row <Var>{error.sheetRow}</Var>: the formula for column{' '}
      <Var>{where}</Var> divides by zero
    </T>
  ),
};

function RowErrorText({ error }: { error: RowError }) {
  const where =
    error.problem === 'divide-by-zero'
      ? (error.column ?? '')
      : (error.header ?? error.column ?? '');
  return <>{ROW_ERROR_TEXT[error.problem](error, where)}</>;
}
